// Shapes returned by the standalone submission queues AND the global
// Submissions tab. Self-contained copies of the classroom Submissions types
// (the classroom file pulls instructor-only imports, and this folder must
// compile in both apps).

type Stamped = {
	created_at: string;
	updated_at: string;
};

/** A classroom or a standalone course, as the list preloads it. */
export type ParentProduct = {
	id: number;
	title: string;
	label?: string;
};

/**
 * The course (classroom course or standalone lecture) a submission lives in.
 * `classroom_id` / `standalone_course_id` decide which action route applies
 * (see endpoints.ts); the preloaded parent carries the product title.
 */
export type CourseIdentity = {
	id: number;
	name: string;
	classroom_id?: number | null;
	standalone_course_id?: number | null;
	classroom?: ParentProduct | null;
	standaloneCourse?: ParentProduct | null;
};

type QuestionTest = {
	id?: number;
	unit_id?: number;
	uuid?: string;
	title?: string;
	duration?: number;
	unit: {
		name: string;
		course_id: number;
		id: number;
		uuid: string;
		course: CourseIdentity;
	};
};

type Question = {
	answer_schema: string;
	content: string;
	feedback: string;
	id: number;
	order: number;
	parsed_content: string;
	test_id: number;
	type: string;
	weight: number;
} & Stamped;

type AnswerContent = {
	correct?: boolean;
	id?: number;
	options: {
		/** null = the student left it blank (a placeholder row) */
		answer: string | null;
		type: string;
	};
} & Partial<Stamped>;

/**
 * One essay slot of an attempt. The list serves the attempt's SERVED essay set:
 * a stored answer row, or — for a served essay the student never answered — a
 * placeholder with `id: null` and no score. Grading a placeholder creates the
 * row; key rows by `question.id`, never by `id`.
 */
export type AttemptAnswer = {
	correct: boolean | null;
	id: number | null;
	placeholder?: boolean;
	score: number | null;
	test_attempt_id: number;
	test_question_id: number;
	question: Question;
	content: AnswerContent;
	// Per-answer instructor feedback (HTML), null until graded
	feedback?: string | null;
} & Partial<Stamped>;

export type TestSubmission = {
	active: boolean;
	auto_evaluate: boolean;
	end_date: string;
	evaluation_status: string;
	grade: string;
	id: number;
	overall_score: number;
	score: number;
	status: string;
	student_id: number;
	test_id: number;
	test_model_id: number;
	answers: Array<AttemptAnswer>;
	answersCount: string;
	test: QuestionTest;
	user: {
		first_name: string;
		id: number;
		last_name: string;
		username: string;
	};
} & Stamped;

export type StandaloneAssignment = {
	id: number;
	unit_id: number;
	uuid: string;
	duration: number;
	overall_score: number;
	show_status: boolean;
	question: string;
	question_type: 'attachment' | 'text';
	unit: {
		name: string;
		course_id: number;
		id: number;
		course: CourseIdentity;
	};
} & Stamped;

export type AssignmentSubmission = {
	id: number;
	assignment_id: number;
	student_id: number;
	status: 'failed' | 'passed';
	score?: number;
	feedback?: string;
	content: string;
	content_type: 'attachment' | 'text';
	user: {
		username: string;
		first_name: string;
		last_name: string;
		id: number;
	};
	assignment: StandaloneAssignment;
} & Stamped;

export type GradeQuestionPayload = {
	question_id: number;
	attempt_id: number;
	score: number;
	correct?: boolean;
	weight: number;
	// Per-answer instructor feedback (HTML from the RichTextEditor)
	feedback?: string;
	submitted: boolean | null;
};

export type ScoreAssignmentPayload = {
	submission_id: number;
	score: number | null;
	overall?: number;
	status: 'passed' | 'failed' | null;
	feedback: string;
};

/**
 * The per-row action calls a grading modal needs. Resolved from the row's
 * course (classroom route vs standalone-lecture route) by endpoints.ts.
 */
export type BulkVerdict = 'full' | 'zero';

export type SubmissionEndpoints = {
	gradeQuestion: (payload: GradeQuestionPayload) => Promise<unknown>;
	closeAttempt: (attemptId: number) => Promise<unknown>;
	/** Overwrites every essay slot with full/zero score (no feedback) and closes. */
	bulkGrade: (attemptId: number, verdict: BulkVerdict) => Promise<unknown>;
	scoreAssignment: (payload: ScoreAssignmentPayload) => Promise<unknown>;
};

export type SubmissionsMeta = {
	total?: number;
	per_page?: number;
	current_page?: number;
	last_page?: number;
};

/** What the global tab shows in its product badge. */
export function productOf(course: CourseIdentity | undefined): {
	kind: 'classroom' | 'standalone' | 'unknown';
	title: string;
} {
	if (!course) return { kind: 'unknown', title: '' };
	if (course.classroom_id && course.classroom) {
		return { kind: 'classroom', title: course.classroom.title };
	}
	if (course.standalone_course_id && course.standaloneCourse) {
		return { kind: 'standalone', title: course.standaloneCourse.title };
	}
	return { kind: course.classroom_id ? 'classroom' : course.standalone_course_id ? 'standalone' : 'unknown', title: '' };
}
